/**
 * Shared resolution for `/audit-trail/preview-current-node` titles (see useCurrentNodeData).
 * Copy/move/delete review uses the same rules so the app bar matches that screen.
 */

import {
  findBulkBlockByPreviewId,
  isSnapshotRootFirstEntryDualEnvelope,
  isUuidKeyedBulkBlock,
  listMainEntryBulkOperationBlockKeys,
} from './fetchSnapshotContent';

export const isTruthyMainEntry = (value) =>
  value === true ||
  (typeof value === "string" && value.trim().toLowerCase() === "true");

/**
 * @param {{ copyReplaceBulk?: boolean, auditFirstEntryFlag?: boolean, mainEntryBulk?: boolean }} [snapOpts] — Copy & replace only; omit for legacy callers.
 */
export const parseSnapshotData = (
  snapshotData,
  isMainEntry,
  previewTreeNodeId,
  snapOpts = {},
) => {
  if (
    !snapshotData ||
    typeof snapshotData !== "object" ||
    Array.isArray(snapshotData)
  ) {
    return { versionContent: null, sections: [] };
  }
  let vc = {};

  let content = [];

  if ("data" in snapshotData) {
    content = snapshotData.data;
    vc = { ...snapshotData, data: undefined };
  } else {
    const versionContentRoot =
      snapshotData.version_content &&
      typeof snapshotData.version_content === "object" &&
      !Array.isArray(snapshotData.version_content)
        ? snapshotData.version_content
        : null;
    vc = versionContentRoot;
    let block = null;
    const copyReplace = snapOpts.copyReplaceBulk === true;
    const auditFirstEntryFlag = snapOpts.auditFirstEntryFlag === true;
    const mainEntryBulk = snapOpts.mainEntryBulk === true;
    const fe =
      snapshotData.first_entry &&
      typeof snapshotData.first_entry === "object" &&
      !Array.isArray(snapshotData.first_entry)
        ? snapshotData.first_entry
        : null;
    const feDual = fe && isSnapshotRootFirstEntryDualEnvelope(fe);
    const cond2 = copyReplace && auditFirstEntryFlag;
    const cond1 = copyReplace && mainEntryBulk && !auditFirstEntryFlag;

    if (cond2) {
      content = Array.isArray(fe?.newContent) ? fe.newContent : [];
    } else if (isMainEntry) {
      if (cond1) {
        const orderedKeys = listMainEntryBulkOperationBlockKeys(snapshotData);
        const lastKey =
          orderedKeys.length > 0 ? orderedKeys[orderedKeys.length - 1] : null;
        block = lastKey ? snapshotData[lastKey] : null;
        content =
          block?.new_content ??
          block?.newContent ??
          block?.["new content"] ??
          null;
      } else if (!copyReplace && feDual) {
        content = Array.isArray(fe.newContent) ? fe.newContent : [];
      } else {
        const orderedKeys = listMainEntryBulkOperationBlockKeys(snapshotData);
        const lastKey =
          orderedKeys.length > 0 ? orderedKeys[orderedKeys.length - 1] : null;
        block = lastKey ? snapshotData[lastKey] : null;
        content =
          block?.new_content ??
          block?.newContent ??
          block?.["new content"] ??
          null;
      }
    } else if (
      previewTreeNodeId &&
      (!copyReplace || (!mainEntryBulk && !auditFirstEntryFlag))
    ) {
      const focusId = String(previewTreeNodeId).trim();
      block = focusId ? snapshotData[focusId] : null;
      if (!isUuidKeyedBulkBlock(block)) {
        block = focusId ? findBulkBlockByPreviewId(snapshotData, focusId) : null;
      }
      content =
        block?.new_content ?? block?.newContent ?? block?.["new content"] ?? null;
    } else {
      content = null;
    }
  }

  return {
    versionContent: vc,
    sections: Array.isArray(content) ? content : [],
  };
};

const CHILD_ACTION_MAP = {
  ATTACHMENT_MOVED: "attachment",
  VIDEO_MOVED: "video",
  "RICH-TEXT_MOVED": "rich-text",
  CANVAS_MOVED: "canva",
  ATTACHMENT_COPIED: "attachment",
  VIDEO_COPIED: "video",
  "RICH-TEXT_COPIED": "rich-text",
  CANVAS_COPIED: "canva",
  CANVA_COPIED: "canva",
};

const findChildInSections = (
  sections,
  previewTreeNodeId,
  resolvedType,
  empty,
  treeField = "tree_node_id",
) => {
  let cumSteps = 0;
  let globalTypeCounter = 0;

  for (let sIdx = 0; sIdx < sections.length; sIdx++) {
    const sec = sections[sIdx];
    const steps = sec?.steps ?? [];
    for (let stIdx = 0; stIdx < steps.length; stIdx++) {
      const step = steps[stIdx];
      const stepChildren = step?.children ?? [];
      const sameTypeStepChildren = stepChildren.filter(
        (ch) => ch?.type === resolvedType,
      );

      for (let i = 0; i < sameTypeStepChildren.length; i++) {
        if (sameTypeStepChildren[i]?.[treeField] === previewTreeNodeId) {
          const chIdx = stepChildren.findIndex(
            (ch) => ch?.[treeField] === previewTreeNodeId,
          );
          return {
            ...empty,
            type: resolvedType,
            sectionIndex: sIdx,
            section: sec,
            stepIndex: stIdx,
            childIndex: chIdx,
            childNode: stepChildren[chIdx],
            globalChildNumber: globalTypeCounter + i + 1,
            globalStepStart: cumSteps,
          };
        }
      }
      globalTypeCounter += sameTypeStepChildren.length;

      const substeps = step?.substeps ?? [];
      for (let subIdx = 0; subIdx < substeps.length; subIdx++) {
        const substep = substeps[subIdx];
        const subChildren = substep?.children ?? [];
        const sameTypeSubChildren = subChildren.filter(
          (ch) => ch?.type === resolvedType,
        );

        for (let i = 0; i < sameTypeSubChildren.length; i++) {
          if (sameTypeSubChildren[i]?.[treeField] === previewTreeNodeId) {
            const subChIdx = subChildren.findIndex(
              (ch) => ch?.[treeField] === previewTreeNodeId,
            );
            return {
              ...empty,
              type: resolvedType,
              sectionIndex: sIdx,
              section: sec,
              stepIndex: stIdx,
              substepIndex: subIdx,
              childIndex: subChIdx,
              childNode: subChildren[subChIdx],
              globalChildNumber: globalTypeCounter + i + 1,
              globalStepStart: cumSteps,
            };
          }
        }
        globalTypeCounter += sameTypeSubChildren.length;
      }
    }
    cumSteps += steps.length;
  }
  return null;
};

/**
 * @param {unknown} versionContentOrActionStr - Snapshot `versionContent` from {@link parseSnapshotData}, or `action_to_show` string.
 */
export const resolveMovedCopyNode = (
  actionToShow,
  sections,
  previewTreeNodeId,
  snapshotData,
  versionContentOrActionStr = " ",
) => {
  const empty = {
    type: null,
    sectionIndex: -1,
    section: null,
    stepIndex: -1,
    substepIndex: -1,
    childIndex: -1,
    childNode: null,
    globalStepStart: 0,
  };
  const versionContentObj =
    versionContentOrActionStr &&
    typeof versionContentOrActionStr === "object" &&
    !Array.isArray(versionContentOrActionStr)
      ? versionContentOrActionStr
      : null;
  const vcActionStr = versionContentObj
    ? String(versionContentObj.action_to_show ?? "")
    : typeof versionContentOrActionStr === "string"
      ? versionContentOrActionStr
      : "";
  const isNotRevCreatedEntry = !(
    vcActionStr.includes("Created") && vcActionStr.includes("Rev")
  );
  const isNotCopiedReplacedEntry =
    !/user\s+copied\s+and\s+replaced/i.test(vcActionStr) &&
    !/\bcopied\s+and\s+replaced\b/i.test(vcActionStr);

  if (actionToShow === "REVISION_COPIED") {
    return {
      ...empty,
      type: "revision",
      sections,
      version_content: versionContentObj ?? {},
    };
  }

  if (sections.length === 0 || !previewTreeNodeId) return empty;

  if (actionToShow === "SECTION_MOVED" || actionToShow === "SECTION_COPIED") {
    const secIdx = sections.findIndex((s) => {
      if (
        actionToShow === "SECTION_COPIED" &&
        isNotRevCreatedEntry &&
        isNotCopiedReplacedEntry
      )
        return s?.previous_tree_node_id === previewTreeNodeId;

      return s?.tree_node_id === previewTreeNodeId;
    });
    if (secIdx < 0) return empty;
    const globalStepStart = sections
      .slice(0, secIdx)
      .reduce((sum, s) => sum + (s?.steps?.length || 0), 0);
    return {
      ...empty,
      type: "section",
      sectionIndex: secIdx,
      section: sections[secIdx],
      globalStepStart,
    };
  }

  if (actionToShow === "STEP_MOVED" || actionToShow === "STEP_COPIED") {
    let cumSteps = 0;
    for (let sIdx = 0; sIdx < sections.length; sIdx++) {
      const sec = sections[sIdx];
      const steps = sec?.steps ?? [];
      const stIdx = steps.findIndex((st) => {
        if (
          actionToShow === "STEP_COPIED" &&
          isNotRevCreatedEntry &&
          isNotCopiedReplacedEntry
        )
          return st?.previous_tree_node_id === previewTreeNodeId;

        return st?.tree_node_id === previewTreeNodeId;
      });
      if (stIdx >= 0) {
        return {
          ...empty,
          type: "step",
          sectionIndex: sIdx,
          section: sec,
          stepIndex: stIdx,
          globalStepStart: cumSteps,
        };
      }
      cumSteps += steps.length;
    }
  }

  if (actionToShow === "SUBSTEP_MOVED" || actionToShow === "SUBSTEP_COPIED") {
    let cumSteps = 0;
    for (let sIdx = 0; sIdx < sections.length; sIdx++) {
      const sec = sections[sIdx];
      const steps = sec?.steps ?? [];
      for (let stIdx = 0; stIdx < steps.length; stIdx++) {
        const step = steps[stIdx];
        const substeps = step?.substeps ?? [];
        const subIdx = substeps.findIndex((sub) => {
          if (
            actionToShow === "SUBSTEP_COPIED" &&
            isNotRevCreatedEntry &&
            isNotCopiedReplacedEntry
          )
            return sub?.previous_tree_node_id === previewTreeNodeId;

          return sub?.tree_node_id === previewTreeNodeId;
        });
        if (subIdx >= 0) {
          return {
            ...empty,
            type: "substep",
            sectionIndex: sIdx,
            section: sec,
            stepIndex: stIdx,
            substepIndex: subIdx,
            globalStepStart: cumSteps,
          };
        }
      }
      cumSteps += steps.length;
    }
  }

  const childType = CHILD_ACTION_MAP[actionToShow];
  if (childType) {
    const result = findChildInSections(
      sections,
      previewTreeNodeId,
      childType,
      empty,
      actionToShow.includes("_COPIED") &&
        !("data" in snapshotData) &&
        isNotRevCreatedEntry &&
        isNotCopiedReplacedEntry
        ? "previous_tree_node_id"
        : "tree_node_id",
    );
    if (result) return result;
  }

  return empty;
};

/** True when {@link resolveMovedCopyNode} found a concrete node (not the empty placeholder). */
export function hasMeaningfulPreviewCurrentNodeResolve(resolved) {
  if (!resolved || typeof resolved !== "object") return false;
  if (resolved.type === "revision") return true;
  if (resolved.type === "section") return resolved.sectionIndex >= 0;
  if (resolved.type === "step") return resolved.stepIndex >= 0;
  if (resolved.type === "substep") return resolved.substepIndex >= 0;
  if (
    resolved.type &&
    ["attachment", "video", "rich-text", "canva"].includes(resolved.type)
  ) {
    return !!resolved.childNode;
  }
  return false;
}

/** Node label only (no version suffix) — matches preview-current-node before `(Version …)`. */
export function buildPreviewCurrentNodeLabelOnly(resolved) {
  if (resolved.type === "revision" && resolved.version_content) {
    const vc = resolved.version_content;
    const label = `${vc.item_code_type ?? ""} ${vc.partNumber ?? ""}`.trim();
    return label || "Preview";
  }
  if (resolved.type === "section" && resolved.sectionIndex >= 0) {
    return `Section ${resolved.sectionIndex + 1}`;
  }
  if (resolved.type === "step" && resolved.stepIndex >= 0) {
    return `Step ${resolved.globalStepStart + resolved.stepIndex + 1}`;
  }
  if (resolved.type === "substep" && resolved.substepIndex >= 0) {
    const stepNum = resolved.globalStepStart + resolved.stepIndex + 1;
    const substepLetter = String.fromCharCode(65 + resolved.substepIndex);
    return `Substep ${stepNum}${substepLetter}`;
  }
  const childLabelMap = {
    attachment: "Attachment",
    video: "Video",
    "rich-text": "Rich Text",
    canva: "Canvas",
  };
  if (childLabelMap[resolved.type] && resolved.childNode) {
    return `${childLabelMap[resolved.type]} ${
      resolved.globalChildNumber ? `${resolved.globalChildNumber} ` : ""
    }`.trim();
  }
  return "Preview";
}

/**
 * @param {{ suffixWord?: string }} [options] — default `Selected` (preview-current-node). Pass `suffixWord: ""` for `Step 1 (Version …)` only.
 */
export function buildDisplayTitle(versionId, resolved, options) {
  if (!versionId) return "Loading...";
  let suffixWord = "Selected";
  if (options && Object.prototype.hasOwnProperty.call(options, "suffixWord")) {
    suffixWord =
      typeof options.suffixWord === "string" ? options.suffixWord.trim() : "";
  }
  const versionPart = `(Version ${versionId})`;
  const label = buildPreviewCurrentNodeLabelOnly(resolved);
  const rest = suffixWord ? `${versionPart} ${suffixWord}` : versionPart;
  return `${label} ${rest}`.replace(/\s+/g, " ").trim();
}
